import { useState, useEffect } from 'react'
import { Tag } from '../../services/api'
import { getTagName } from '../../i18n/translations'
import { productApi } from '../../services/api'

interface TagSelectorProps {
  selectedTagIds: string[]
  onChange: (tagIds: string[]) => void
  placeholder?: string
}

export default function TagSelector({ selectedTagIds, onChange, placeholder = 'Select tags...' }: TagSelectorProps) {
  const [tags, setTags] = useState<Tag[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadTags()
  }, [])

  const loadTags = async () => {
    try {
      const response = await productApi.getTags()
      setTags(response.tags || [])
    } catch (error) {
      console.error('Failed to load tags:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter(id => id !== tagId))
    } else {
      onChange([...selectedTagIds, tagId])
    }
  }

  const removeTag = (tagId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(selectedTagIds.filter(id => id !== tagId))
  }

  const selectedTags = tags.filter(tag => selectedTagIds.includes(tag.id))

  // Group tags by category for better organization
  const groupedTags = {
    'Electronics & Tech': tags.filter(t =>
      ['electronics', 'smartphones', 'laptops', 'pc-hardware', 'photo-video', 'headphones', 'apple'].includes(t.slug)
    ),
    'Fashion & Accessories': tags.filter(t =>
      ['clothing', 'shoes', 'accessories', 'watch', 'parfume', 'beauty'].includes(t.slug)
    ),
    'Home & Living': tags.filter(t =>
      ['home', 'garden', 'furniture', 'kitchen-hardware'].includes(t.slug)
    ),
    'Lifestyle & Hobbies': tags.filter(t =>
      ['books', 'art', 'music', 'fitness', 'food-drinks', 'collectables', 'for-kids', 'education'].includes(t.slug)
    ),
    'Automotive': tags.filter(t =>
      ['cars', 'car-parts'].includes(t.slug)
    ),
    'Services': tags.filter(t =>
      ['service'].includes(t.slug)
    ),
  }

  if (loading) {
    return (
      <div className="bg-gray-50 rounded-lg p-3 text-center text-gray-500">
        Loading tags...
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Selected Tags Display */}
      <div
        className="bg-gray-50 rounded-lg p-3 min-h-[48px] cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedTags.length === 0 ? (
          <span className="text-gray-400">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedTags.map(tag => (
              <div
                key={tag.id}
                className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
              >
                <span className="mr-1">{tag.icon}</span>
                <span>{getTagName(tag.name_key)}</span>
                <button
                  onClick={(e) => removeTag(tag.id, e)}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Content */}
          <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-200 z-20 max-h-96 overflow-y-auto">
            <div className="p-2">
              {/* Quick actions */}
              <div className="flex justify-between items-center p-2 border-b mb-2">
                <span className="text-sm text-gray-600">
                  {selectedTagIds.length} selected
                </span>
                {selectedTagIds.length > 0 && (
                  <button
                    onClick={() => onChange([])}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Grouped Tags */}
              {Object.entries(groupedTags).map(([category, categoryTags]) => {
                if (categoryTags.length === 0) return null

                return (
                  <div key={category} className="mb-3">
                    <div className="text-xs font-semibold text-gray-600 uppercase px-2 mb-1">
                      {category}
                    </div>
                    <div className="space-y-1">
                      {categoryTags.map(tag => {
                        const isSelected = selectedTagIds.includes(tag.id)
                        return (
                          <label
                            key={tag.id}
                            className={`flex items-center p-2 rounded-lg cursor-pointer transition-colors ${
                              isSelected
                                ? 'bg-blue-50 text-blue-800'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleTag(tag.id)}
                              className="mr-3"
                            />
                            <span className="mr-2">{tag.icon}</span>
                            <span className="flex-1">{getTagName(tag.name_key)}</span>
                            {tag.product_count !== undefined && tag.product_count > 0 && (
                              <span className="text-xs text-gray-400">
                                ({tag.product_count})
                              </span>
                            )}
                          </label>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Apply button */}
            <div className="sticky bottom-0 bg-white border-t p-2">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full btn-primary py-2"
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}